import React from "react";
import { CalendarPlus, Apple } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Wedding event constants
const EVENT_START_UTC = "20270519T130000Z"; // 16:00 EEST (UTC+3)
const EVENT_END_UTC = "20270520T000000Z"; // ~03:00 next day EEST — a good long celebration window
const EVENT_LOCATION = "Ammos Kalambouri Beach, Polis Chrysochous, Cyprus";
const EVENT_GEO_QUERY = "Ammos Kalambouri Beach, Polis, Cyprus";
const SITE_URL =
  typeof window !== "undefined" ? window.location.origin : "https://elimag.wedding";

const buildGoogleUrl = (title, details) => {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${EVENT_START_UTC}/${EVENT_END_UTC}`,
    details: `${details}\n\n${SITE_URL}`,
    location: EVENT_LOCATION,
    sf: "true",
    output: "xml",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const buildOutlookUrl = (title, details) => {
  // Outlook web deep-link uses local ISO with -00:00; use UTC.
  const start = "2027-05-19T13:00:00Z";
  const end = "2027-05-20T00:00:00Z";
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: title,
    body: `${details}\n\n${SITE_URL}`,
    location: EVENT_LOCATION,
    startdt: start,
    enddt: end,
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

const buildIcs = (title, details) => {
  // RFC 5545 minimum viable calendar file.
  const uid = "elimag-2027-05-19-wedding@elimag.wedding";
  const escape = (s) =>
    (s || "")
      .replace(/\\/g, "\\\\")
      .replace(/\n/g, "\\n")
      .replace(/,/g, "\\,")
      .replace(/;/g, "\\;");
  const stamp = new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d+/, "");
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Eli & Mag//Wedding Website//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${EVENT_START_UTC}`,
    `DTEND:${EVENT_END_UTC}`,
    `SUMMARY:${escape(title)}`,
    `DESCRIPTION:${escape(`${details}\n\n${SITE_URL}`)}`,
    `LOCATION:${escape(EVENT_LOCATION)}`,
    `URL:${SITE_URL}`,
    "STATUS:CONFIRMED",
    "TRANSP:OPAQUE",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
};

const downloadIcs = (title, details) => {
  const ics = buildIcs(title, details);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "eli-mag-wedding.ics";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const AddToCalendarButton = ({ variant = "primary", className = "" }) => {
  const { t } = useLang();
  const title = t("home.calendarEventTitle");
  const details = t("home.calendarEventDetails");

  const baseTriggerClasses =
    "inline-flex items-center gap-2 h-12 px-6 rounded-xl text-[12px] tracking-[0.22em] uppercase transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent";

  const styleByVariant = {
    primary: "bg-foreground text-background hover:bg-foreground/90",
    onDark: "bg-white text-foreground hover:bg-white/90 [text-shadow:none]",
    ghost:
      "border border-foreground/30 text-foreground/85 hover:text-foreground hover:border-foreground/60",
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={`${baseTriggerClasses} ${styleByVariant[variant] || styleByVariant.primary} ${className}`}
          data-testid="add-to-calendar-trigger"
          aria-label={t("home.addToCalendar")}
        >
          <CalendarPlus className="w-4 h-4" aria-hidden="true" />
          {t("home.addToCalendar")}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="w-64 bg-background border-border"
        data-testid="add-to-calendar-menu"
      >
        <DropdownMenuItem
          onSelect={() => window.open(buildGoogleUrl(title, details), "_blank", "noopener")}
          className="cursor-pointer gap-2"
          data-testid="add-to-calendar-google"
        >
          <CalendarPlus className="w-4 h-4 text-accent" aria-hidden="true" />
          {t("home.calendarGoogle")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => downloadIcs(title, details)}
          className="cursor-pointer gap-2"
          data-testid="add-to-calendar-apple"
        >
          <Apple className="w-4 h-4 text-accent" aria-hidden="true" />
          {t("home.calendarApple")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => window.open(buildOutlookUrl(title, details), "_blank", "noopener")}
          className="cursor-pointer gap-2"
          data-testid="add-to-calendar-outlook"
        >
          <CalendarPlus className="w-4 h-4 text-accent" aria-hidden="true" />
          {t("home.calendarOutlook")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AddToCalendarButton;
